import React, { ReactNode } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'src/component/ui/dialog'
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from 'src/component/ui/drawer'
import { Button, OutlinedButton, PrimaryButton } from 'src/component/ui/button'
import { useMediaQuery } from 'src/hook/use-media-query'

export type SuggestedSeriesDismissDialogProps = {
  isOpen: boolean
  onClose: () => void
  onDismiss: () => void
  isSubmitting?: boolean
  title?: string
  description?: ReactNode
  confirmLabel?: string
}

const footerActionsClass = 'flex flex-wrap gap-3 justify-center w-full sm:w-auto sm:justify-center'

const cancelButtonClass = 'px-8 md:px-10 '

const dismissButtonClass = 'px-8 md:px-10 border-danger text-danger hover:bg-danger/10 hover:text-danger'

const defaultDescription = (
  <div className="text-mist font-roboto-condensed text-base tracking-wide space-y-2 leading-relaxed text-center">
    <p>Once dismissed, you will lose all completed workouts within this series.</p>
    <p>Don&apos;t worry, you can start the series flow again.</p>
  </div>
)

export const SuggestedSeriesDismissDialog = ({
  isOpen,
  onClose,
  onDismiss,
  isSubmitting = false,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Dismiss',
}: SuggestedSeriesDismissDialogProps): React.ReactElement => {
  const isMobile = useMediaQuery('(max-width: 992px)')

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      onClose()
    }
  }

  const descriptionBlock = description ?? defaultDescription

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerContent className="bg-secondary-gray px-6 pb-8 border-0" style={{ pointerEvents: 'auto' }}>
          <DrawerHeader className="text-center space-y-4">
            <DrawerTitle className="font-evogria text-ivory text-2xl uppercase leading-tight font-normal">{title}</DrawerTitle>
            <DrawerDescription asChild>{descriptionBlock}</DrawerDescription>
          </DrawerHeader>
          <DrawerFooter className="flex-col sm:flex-row flex-wrap justify-center gap-3 pt-2 pb-0">
            <div className={footerActionsClass}>
              <PrimaryButton type="button" className={cancelButtonClass} onClick={onClose}>
                Cancel
              </PrimaryButton>
              <Button
                type="button"
                variant="outline"
                className={dismissButtonClass}
                onClick={onDismiss}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                {confirmLabel}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-secondary-gray border-0 max-w-md gap-6" style={{ pointerEvents: 'auto' }}>
        <DialogHeader className="text-center space-y-4 sm:text-center">
          <DialogTitle className="font-evogria text-ivory text-2xl md:text-4xl uppercase leading-tight font-normal">
            {title}
          </DialogTitle>
          <DialogDescription asChild>{descriptionBlock}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row gap-3 sm:justify-center sm:space-x-0">
          <div className={footerActionsClass}>
            <PrimaryButton type="button" className={cancelButtonClass} onClick={onClose}>
              Cancel
            </PrimaryButton>
            <OutlinedButton
              type="button"
              className={dismissButtonClass}
              onClick={onDismiss}
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              {confirmLabel}
            </OutlinedButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
